import { cn } from '@repo/tailwind-config/tailwind.functions'
import { InputType } from '@repo/toolkit/types'
import { useId } from 'react'

export type RadioInputProps = Omit<
  InputType<'autoComplete' | 'value' | 'name' | 'onChange'>,
  'hideLabel' | 'label'
> & {
  label?: string
  labelTextVariant?: 'primary' | 'secondary'
  name?: string
  hasError?: boolean
  labelStyle?: React.CSSProperties
  onCheck: () => void
  labelClassName?: string
  variant?: 'primary' | 'success'
  value?: unknown
}

const variantClasses = {
  primary: {
    border: 'border-primary',
    checkedBorder: 'checked:border-primary',
    checkedBg: 'checked:bg-primary',
    labelText: 'text-primary'
  },
  success: {
    border: 'border-green',
    checkedBorder: 'checked:border-green',
    checkedBg: 'checked:bg-green',
    labelText: 'text-green'
  }
}

const RadioInput = ({
  label,
  hasError,
  labelClassName,
  labelTextVariant = 'primary',
  variant = 'primary',
  onCheck,
  labelStyle,
  value: _value,
  ...inputProps
}: RadioInputProps) => {
  const currentVariant = variantClasses[variant]
  const generatedId = useId()
  // a caller-provided id replaces the generated one on the input, so the label must follow it
  const inputId = inputProps.id ?? generatedId

  return (
    <div
      className={cn('flex items-center', {
        'pointer-events-none': inputProps.readOnly
      })}
    >
      <div
        className={cn('relative h-5 w-5 rounded-full border-2 border-grey-stroke', {
          'border-red': hasError,
          'pointer-events-none border-grey-stroke': inputProps.disabled,
          [currentVariant.border]: inputProps.checked && !inputProps.disabled
        })}
      >
        <input
          type="radio"
          id={inputId}
          className={cn(
            'absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 transform cursor-pointer appearance-none rounded-full  bg-transparent checked:before:absolute checked:before:left-1/2 checked:before:top-1/2 checked:before:h-1/2 checked:before:w-1/2 checked:before:-translate-x-1/2 checked:before:-translate-y-1/2 checked:before:scale-0 checked:before:transform checked:before:rounded-full checked:before:bg-white checked:before:duration-300',
            currentVariant.checkedBorder,
            currentVariant.checkedBg,
            {
              'cursor-not-allowed bg-grey-light checked:!border-grey-light-active checked:!bg-grey-light-active':
                inputProps.disabled
            }
          )}
          {...inputProps}
          onChange={() => {
            if (inputProps.disabled) return
            onCheck()
          }}
        />
      </div>
      <label
        htmlFor={inputId}
        onClick={(e) => {
          e.preventDefault()
          if (inputProps.disabled || inputProps.readOnly) return
          onCheck()
        }}
        style={labelStyle}
        className={cn(
          'duration-300 cursor-pointer pl-2 font-normal flex-1 ',
          {
            [currentVariant.labelText]: inputProps.checked,
            'text-red': hasError,
            'text-grey text-xs': labelTextVariant === 'primary',
            'text-gray-700 text-sm': labelTextVariant === 'secondary',
            '!cursor-not-allowed !text-grey-light-active': inputProps.disabled
          },
          labelClassName
        )}
      >
        {label}
      </label>
    </div>
  )
}

export default RadioInput
